import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { ArrowLeft, FileText, User, Calendar, HelpCircle, Keyboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useBillQueue } from '@/hooks/useBillQueue';
import { useReviewTasks } from '@/hooks/useReviewTasks';
import { useCustomerFieldConfigs } from '@/hooks/useCustomerFieldConfigs';
import EnhancedPDFViewer from '@/components/EnhancedPDFViewer';
import { SimpleManualParser } from '@/components/SimpleManualParser';
import { SafeErrorBoundary } from '@/components/SafeErrorBoundary';
import { ReviewErrorBoundary } from '@/components/ReviewErrorBoundary';
import { CustomerValidationProvider } from '@/components/CustomerValidationProvider';
import { CompactCustomerSelection } from '@/components/CompactCustomerSelection';
import { ValidationProgressCard } from '@/components/ValidationProgressCard';
import { EnhancedDynamicFieldValidator } from '@/components/EnhancedDynamicFieldValidator';
import { useSimpleTextPaste } from '@/hooks/useSimpleTextPaste';

import { useSmartFieldSuggestions } from '@/hooks/useSmartFieldSuggestions';
import { useHybridExtraction } from '@/hooks/useHybridExtraction';
import AITextRecognitionPanel from '@/components/AITextRecognitionPanel';
import { AIFieldSearchPanel } from '@/components/AIFieldSearchPanel';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useRealtimeReviewSession } from '@/hooks/useRealtimeReviewSession';
import { useRealtimeStatus } from '@/hooks/useRealtimeStatus';
import { usePerformanceOptimization } from '@/hooks/usePerformanceOptimization';
import { ReviewAutoSaveIndicator } from '@/components/ReviewAutoSaveIndicator';
import { ReviewPerformanceMonitor } from '@/components/ReviewPerformanceMonitor';
import { MobileReviewInterface } from '@/components/mobile/MobileReviewInterface';
import { ReviewTourProvider, useReviewTour } from '@/components/ReviewTourProvider';
import { AccessibilityEnhancements } from '@/components/AccessibilityEnhancements';
import { useReviewKeyboardShortcuts } from '@/hooks/useReviewKeyboardShortcuts';
import { useIsMobile } from '@/hooks/use-mobile';

function ReviewItemContent() {
  const { queueItemId } = useParams<{ queueItemId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { queueItems, updateQueueItem } = useBillQueue();
  const { createTask, updateTask } = useReviewTasks();
  const isMobile = useIsMobile();
  const { startReviewTour, startFieldValidationTour, startPDFNavigationTour } = useReviewTour();
  
  const [queueItem, setQueueItem] = useState<any>(null);
  const [reviewTask, setReviewTask] = useState<any>(null);
  const [hasCreatedTask, setHasCreatedTask] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [customerPanelOpen, setCustomerPanelOpen] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedText, setSelectedText] = useState<string>('');
  const [aiPanelOpen, setAiPanelOpen] = useState(true);
  const [documentContent, setDocumentContent] = useState<string>('');
  const [fieldAISuggestions, setFieldAISuggestions] = useState<Record<string, { value: string; confidence: number; reasoning?: string }>>({});
  const [searchingFieldName, setSearchingFieldName] = useState<string | null>(null);
  const [fieldConfidence, setFieldConfidence] = useState<Record<string, number>>({});
  const [extractionHistory, setExtractionHistory] = useState<Array<{field: string; status: 'accepted' | 'suggested' | 'rejected'; confidence: number}>>([]);
  
  // Get field configurations for double-click to paste
  const { configs: fieldConfigs } = useCustomerFieldConfigs(selectedCustomer?.id);
  
  // Smart field suggestions with AI
  const smartSuggestions = useSmartFieldSuggestions();
  
  // Hybrid extraction system
  const hybridExtraction = useHybridExtraction();
  

  // Enhanced double-click to paste functionality with AI integration (backwards compatibility)
  const enhancedPasteHook = useSimpleTextPaste({
    onPaste: (text, fieldName) => {
      if (fieldName) {
        setQueueItem(prev => ({
          ...prev,
          extracted_data: {
            ...prev.extracted_data,
            [fieldName]: text
          }
        }));
        toast({
          title: "Text Pasted",
          description: `"${text.slice(0, 50)}${text.length > 50 ? '...' : ''}" pasted to ${fieldName}`,
        });
        
        // Record feedback for AI learning
        smartSuggestions.recordUserFeedback(
          `manual-paste-${Date.now()}`,
          'accepted',
          text
        );
      }
    },
  });

  // Handle AI analysis results
  const handleAIAnalysis = (analysis: any) => {
    console.log('AI Analysis received:', analysis);
    
    // Generate smart suggestions based on available fields
    if (analysis.text && fieldConfigs.length > 0) {
      const availableFields = fieldConfigs.map(config => ({
        fieldName: config.field_name,
        displayName: config.display_name || config.field_name,
        fieldType: (config.field_type as 'text' | 'number' | 'date' | 'email' | 'tel') || 'text',
        isRequired: config.is_mandatory || false,
        priority: typeof config.priority === 'number' ? config.priority : 5
      }));
      
      smartSuggestions.generateSuggestions(analysis.text, {
        documentType: 'utility_bill',
        availableFields,
        previousSelections: []
      });
    }
  };

  // Handle field suggestion application
  const handleFieldSuggestion = (fieldName: string, text: string) => {
    console.log('handleFieldSuggestion called:', { fieldName, text });
    
    // Update queue item state
    setQueueItem(prev => {
      const updated = {
        ...prev,
        extracted_data: {
          ...prev.extracted_data,
          [fieldName]: text
        }
      };
      console.log('Updated queue item:', updated);
      return updated;
    });
    
    
    // Update hybrid extraction session if available
    if (hybridExtraction?.updateFieldValue) {
      hybridExtraction.updateFieldValue(fieldName, text);
    }
    
    console.log('Field suggestion applied successfully');
  };

  
  // Refs for keyboard navigation
  const amountFieldRef = useRef<HTMLInputElement>(null);
  const dateFieldRef = useRef<HTMLInputElement>(null);
  const supplierFieldRef = useRef<HTMLInputElement>(null);
  const accountFieldRef = useRef<HTMLInputElement>(null);
const focusSuggestTimeoutRef = useRef<NodeJS.Timeout>();

// Track focused field for AI suggestions
useEffect(() => {
  const field = enhancedPasteHook.focusedField;
  if (!field) return;
  
  console.log('Field focused:', field);
  // Simplified focus tracking without auto-suggestions
}, [enhancedPasteHook.focusedField]);

// Enhanced hooks for real-time and performance
  const { sessionData, autoSaveState, updateSessionData, saveSession, toggleAutoSave } = 
    useRealtimeReviewSession(queueItemId || '');
  const { processingStatus, isConnected } = useRealtimeStatus(queueItemId || '');
  const { metrics, settings, updateSettings } = usePerformanceOptimization();

  useEffect(() => {
    if (queueItemId && queueItems.length > 0 && !hasCreatedTask) {
      const item = queueItems.find(q => q.id === queueItemId);
      if (item) {
        setQueueItem(item);
        // Only create task once
        createTask({
          queue_item_id: item.id,
          task_type: 'validation_review',
          priority: 50,
          original_data: item.extracted_data
        });
        setHasCreatedTask(true);
        
        // Auto-trigger hybrid extraction if we have extracted text
        if (item.extracted_data?.extracted_text && !hybridExtraction.session) {
          startHybridExtraction(item);
        }
      } else {
        toast({
          title: "Error",
          description: "Queue item not found",
          variant: "destructive",
        });
        navigate('/dashboard/bill-queue');
      }
    }
  }, [queueItemId, queueItems, hasCreatedTask, hybridExtraction.session]);

  // Auto-trigger hybrid extraction
  const startHybridExtraction = async (item: any) => {
    if (!item.extracted_data?.extracted_text) return;
    
    console.log('Starting hybrid extraction for queue item:', item.id);
    
    await hybridExtraction.startExtraction(
      item.extracted_data.extracted_text,
      {
        queueItemId: item.id,
        sessionType: 'full_extraction',
        existingData: item.extracted_data
      }
    );
  };

  const handleBack = () => {
    navigate('/dashboard/bill-queue');
  };

  const handleSave = async (editedData: any, notes?: string) => {
    if (!queueItem) return;

    try {
      await updateQueueItem({
        id: queueItem.id,
        data: {
          extracted_data: editedData,
          processing_status: 'review_required',
          notes: notes
        }
      });

      setQueueItem(prev => ({
        ...prev,
        extracted_data: editedData,
        reviewer_notes: notes
      }));

      toast({
        title: "Success",
        description: "Changes saved successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
      });
    }
  };

  const handleComplete = async () => {
    if (!queueItem) return;

    try {
      await updateQueueItem({
        id: queueItem.id,
        data: {
          processing_status: 'completed'
        }
      });

      if (reviewTask) {
        await updateTask(reviewTask.id, {
          status: 'completed',
          completed_at: new Date().toISOString()
        });
      }

      toast({
        title: "Success",
        description: "Review completed successfully",
      });

      navigate('/dashboard/bill-queue');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to complete review",
        variant: "destructive",
      });
    }
  };

  // Enhanced keyboard shortcuts for review interface
  const { showReviewShortcuts } = useReviewKeyboardShortcuts({
    onSave: () => handleSave(queueItem?.extracted_data),
    onComplete: handleComplete,
    onBack: handleBack,
    onFocusField: (fieldName: string) => {
      const fieldRefs = {
        amount: amountFieldRef,
        date: dateFieldRef,
        supplier: supplierFieldRef,
        account: accountFieldRef
      };
      fieldRefs[fieldName as keyof typeof fieldRefs]?.current?.focus();
    },
    onToggleCustomerPanel: () => setCustomerPanelOpen(prev => !prev),
    onZoomIn: () => {
      const event = new CustomEvent('pdf-zoom-in');
      document.dispatchEvent(event);
    },
    onZoomOut: () => {
      const event = new CustomEvent('pdf-zoom-out');
      document.dispatchEvent(event);
    },
    onNextPage: () => {
      const event = new CustomEvent('pdf-next-page');
      document.dispatchEvent(event);
    },
    onPrevPage: () => {
      const event = new CustomEvent('pdf-prev-page');
      document.dispatchEvent(event);
    }
  });

  if (!queueItem) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading review item...</p>
        </div>
      </div>
    );
  }

  // Fixed URL construction logic - prioritize file_path over file_url
  const getFileUrl = () => {
    // Use file_path first since that's what's populated in the database
    const filePath = queueItem.file_path || queueItem.file_url;
    
    if (!filePath) {
      console.warn('❌ No file path found for queue item:', queueItem.id);
      return null;
    }
    
    // If it's already a full URL, return as is
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    try {
      const isPdf = queueItem.file_name?.toLowerCase().includes('.pdf');
      
      // For PDFs, use simple public URL without image transformations
      if (isPdf) {
        const { data } = supabase.storage.from('bills').getPublicUrl(filePath);
        return data.publicUrl;
      } else {
        // For images, apply transformations
        const { data } = supabase.storage.from('bills').getPublicUrl(filePath, {
          transform: {
            quality: 80
          }
        });
        return data.publicUrl;
      }
    } catch (error) {
      console.error('💥 Exception constructing file URL:', error);
      toast({
        title: "File Access Error",
        description: "Unable to access the uploaded file. Please check the file upload.",
        variant: "destructive",
      });
      return null;
    }
  };

  const fileUrl = getFileUrl();
  const isPdfFile = queueItem.file_name?.toLowerCase().includes('.pdf');
  const hasPdf = fileUrl && isPdfFile;

  // Mobile interface
  if (isMobile) {
    return (
      <MobileReviewInterface
        queueItem={queueItem}
        onSave={handleSave}
        onComplete={handleComplete}
        onBack={handleBack}
        fileUrl={fileUrl}
        isPdfFile={isPdfFile}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col" data-tour="review-interface">
      {/* Header */}
      <Card className="border-b rounded-none">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Queue
            </Button>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <h1 className="text-xl font-semibold">{queueItem.file_name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={startReviewTour}
                title="Start Review Tour"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={showReviewShortcuts}
                title="Show Keyboard Shortcuts"
              >
                <Keyboard className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {new Date(queueItem.created_at).toLocaleDateString()}
            </div>
            <Badge variant={queueItem.status === 'completed' ? 'default' : 'secondary'}>
              {queueItem.status}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Main Content - Clean 50/50 Split Layout */}
      <div className="flex-1 flex h-full">
        <ReviewErrorBoundary
          onError={(error, errorInfo) => {
            console.error('Review interface error:', error, errorInfo);
            toast({
              title: "Component Error",
              description: "A component error occurred. Please try refreshing the page.",
              variant: "destructive",
            });
          }}
        >
          {hasPdf ? (
            // PDF + Form Layout (50/50 split, no cards or extra padding)
            <>
              {/* Left: Enhanced PDF Viewer with AI */}
              <div className="w-1/2 h-full flex flex-col" data-tour="pdf-viewer">
                <div className="flex-1">
                  <EnhancedPDFViewer 
                    pdfUrl={fileUrl!}
                    fileName={queueItem.original_file_name || 'document.pdf'}
                    onTextSelect={(text) => { setSelectedText(text); enhancedPasteHook.handleTextSelect(text); }}
                    onAIAnalysis={(analysis) => {
                      console.log('AI analysis:', analysis);
                      // Handle AI analysis results
                      if (analysis.text && analysis.analysis?.category) {
                        // Use smart field suggestions based on AI analysis
                        const suggestions = [{ 
                          field: analysis.analysis.category, 
                          confidence: analysis.analysis.confidence,
                          extractedValue: analysis.text 
                        }];
                        if (suggestions.length > 0 && suggestions[0].extractedValue) {
                          const topSuggestion = suggestions[0];
                          handleFieldSuggestion(topSuggestion.field, topSuggestion.extractedValue);
                        }
                      }
                    }}
                    enableAIFeatures={true}
                  />
                </div>
                
                {/* AI Panels */}
                <div className="h-80 border-t flex">
                  <div className="w-1/2 border-r">
                    <AITextRecognitionPanel
                      selectedText={selectedText}
                      onFieldSuggestion={handleFieldSuggestion}
                      availableFields={fieldConfigs.map(config => ({
                        name: config.field_name,
                        displayName: config.display_name || config.field_name,
                        type: config.field_type || 'text',
                        required: config.is_mandatory || false
                      }))}
                      documentContent={documentContent}
                    />
                  </div>
                  <div className="w-1/2">
                    <AIFieldSearchPanel
                      extractedText={queueItem.extracted_data?.extracted_text || documentContent}
                      onFieldUpdate={(fieldName, value) => {
                        setQueueItem(prev => ({
                          ...prev,
                          extracted_data: {
                            ...prev.extracted_data,
                            [fieldName]: value
                          }
                        }));
                        hybridExtraction.updateFieldValue(fieldName, value);
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Right: Form Fields */}
              <CustomerValidationProvider>
                <div className="w-1/2 h-full flex flex-col border-l">
                  {/* Customer Assignment */}
                  <div className="p-4 border-b bg-muted/30" data-tour="customer-selection">
                    <h3 className="text-sm font-medium mb-2">Customer Assignment</h3>
                    <CompactCustomerSelection
                      selectedCustomerId={queueItem.customer_id}
                      onCustomerSelect={(customer) => {
                        setSelectedCustomer(customer);
                        setQueueItem(prev => ({
                          ...prev,
                          customer_id: customer?.id
                        }));
                      }}
                    />
                  </div>
                  
                  {/* Text Selection Feedback */}
                  {enhancedPasteHook.selectedText && (
                    <div className="p-3 border-b bg-muted/50">
                      <div className="text-xs text-muted-foreground">
                        Selected: "{enhancedPasteHook.selectedText.substring(0, 50)}{enhancedPasteHook.selectedText.length > 50 ? '...' : ''}"
                      </div>
                      {enhancedPasteHook.focusedField && (
                        <div className="text-xs text-primary mt-1">
                          Target: {enhancedPasteHook.focusedField}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Enhanced Field Validation */}
                  <div className="flex-1 p-4 overflow-y-auto" data-tour="field-validation" style={{ isolation: 'isolate' }}>
                    <div className="mb-3">
                      <h3 className="text-sm font-medium mb-1">Field Validation</h3>
                      <p className="text-xs text-muted-foreground">
                        💡 Click any field, then select text in the PDF to auto-paste. Fields are always focusable.
                      </p>
                    </div>
                    <EnhancedDynamicFieldValidator
                      data={queueItem.extracted_data || {}}
                      onFieldChange={(fieldName, value) => {
                        setQueueItem(prev => ({
                          ...prev,
                          extracted_data: {
                            ...prev.extracted_data,
                            [fieldName]: value
                          }
                        }));
                        updateSessionData?.({ extracted_data: { ...queueItem?.extracted_data, [fieldName]: value } });
                      }}
                      enhancedPasteHook={{
                        getFieldProps: () => ({}),
                        focusedField: enhancedPasteHook.focusedField,
                        selectedText: enhancedPasteHook.selectedText,
                        showPasteFeedback: false,
                        pasteSuccess: false,
                        pasteToField: () => {}
                      }}
                      fieldAISuggestions={fieldAISuggestions}
                      onApplyAISuggestion={(fieldName, value) => handleFieldSuggestion(fieldName, value)}
                      searchingFieldName={searchingFieldName}
                      isSearching={hybridExtraction?.isSearching}
                      fieldConfidence={fieldConfidence}
                      onRejectAISuggestion={(fieldName) => {
                        setFieldAISuggestions(prev => { const copy = { ...prev }; delete copy[fieldName]; return copy; });
                      }}
                    />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="border-t p-4 bg-muted/30 space-y-2" data-tour="review-actions">
                    <Button 
                      onClick={() => handleSave(queueItem.extracted_data)} 
                      variant="outline"
                      className="w-full"
                    >
                      Save Progress
                    </Button>
                    <Button 
                      onClick={handleComplete}
                      className="w-full"
                    >
                      Complete Review
                    </Button>
                  </div>
                </div>
              </CustomerValidationProvider>
            </>
          ) : (
            // Form Only Layout (centered) - With validation provider
            <CustomerValidationProvider>
              <div className="w-full flex flex-col">
                {/* Customer Assignment Section (compact) */}
                <div className="border-b bg-card p-4">
                  <h3 className="text-lg font-medium mb-3">Customer Assignment</h3>
                  <CompactCustomerSelection
                    selectedCustomerId={queueItem.customer_id}
                    onCustomerSelect={(customer) => {
                      setQueueItem(prev => ({
                        ...prev,
                        customer_id: customer?.id
                      }));
                    }}
                  />
                </div>
                
                {/* Manual Entry Form */}
                <div className="flex-1 overflow-auto p-6">
                  <div className="max-w-4xl mx-auto">
                    <SimpleManualParser
                      queueItem={queueItem}
                      onComplete={handleComplete}
                      onSave={() => handleSave(queueItem.extracted_data)}
                    />
                  </div>
                </div>
              </div>
            </CustomerValidationProvider>
          )}
        </ReviewErrorBoundary>
      </div>
      
      {/* Accessibility Enhancements */}
      <AccessibilityEnhancements />
    </div>
  );
}

// Wrapper component with tour provider
export default function ReviewItem() {
  return (
    <ReviewTourProvider>
      <ReviewItemContent />
    </ReviewTourProvider>
  );
}